import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { ForwardModal } from '../../modals/forward';
import { CallPattern } from '../../types';

export type Payload = {
  callPattern: CallPattern;
  display_number: string;
  noAnswerNodeId: null | string;
  numbers: PhoneNumber[];
  recordCall: boolean;
};

export type PhoneNumber = {
  countryCode: null | string;
  id: string;
  isValid: boolean;
  phoneNumber: null | string;
  sip: null | string;
  timeout: null | string;
  type: string;
  user: null | string;
};

type ForwardNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type ForwardNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: ForwardNodeAttributes['translatePayload'] = (node) => {
  const { noAnswerNodeId } = node.payload;

  return noAnswerNodeId
    ? [
        {
          id: uuid(),
          connection: noAnswerNodeId,
          label: 'No answer',
        },
      ]
    : [];
};

const updateConnection: ForwardNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const payload = { ...node.payload };

  if (connection.connectionLabel === 'No answer') {
    payload.noAnswerNodeId = connectionId;
  }

  return { ...node, payload };
};

export const ForwardNodeAttributes: ForwardNodeAttributes = {
  iconName: 'phone-outgoing',
  label: 'Forward',
  helpText:
    'Set up incoming calls to be forwarded to internal or external phone numbers.',
  color: hexStringToNumber(flowNodeColor.green),
  translatePayload,
  updateConnection,
  NodeContent: ForwardNode,
  ActionModal: ForwardModal,
};

export function ForwardNode({ node }: ForwardNodeProps): JSX.Element {
  const { payload } = node;
  const { numbers } = payload;

  const pattern =
    payload.callPattern === CallPattern.Sequence
      ? 'Sequence'
      : 'First to pick up';

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextBreak>
            <Text weight="normal" text="Call pattern" />
            <Text weight="semibold" text={`${pattern},`} />
            <Text weight="normal" text="to" />
            <Text
              weight="semibold"
              text={`${numbers.length} ${
                numbers.length === 1 ? 'phone' : 'phones'
              }`}
            />
          </TextBreak>
        ),
        [pattern, numbers.length],
      )}
    </TextContent>
  );
}
